/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 可编辑表格组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-19 13:48:25
 */
import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  useCallback
} from 'react'
import { Table, Input, DatePicker, Form, message, Button } from 'antd'
import dayjs from 'dayjs'
import type { ColumnProps } from 'antd/lib/table'
import { XC_RECOGNITION_STATUS } from '../types/contextInterface'
import type { RecognitionRecord } from '../types/contextInterface'
import HazardSourceSelector, { XCMonitorType } from './HazardSourceSelector'
import { XCHazardSourceMode } from './HazardSourceSelector'
import './EditableTable.css'
import iconRelevance from '../assets/icon_relevance.png'
import { getBaseColumnsData } from '../lib/columnConfigs'

const { TextArea } = Input

const EditableContext = React.createContext<any>(null)

interface XCEditableColumn extends ColumnProps<RecognitionRecord> {
  editable?: boolean | ((record: RecognitionRecord, index?: number) => boolean)
  inputType?: string | ((record: RecognitionRecord) => string | undefined)
  required?: boolean
  align?: 'left' | 'right' | 'center'
  feedbackComponent?:
    | React.ReactNode
    | ((record: RecognitionRecord, index?: number) => React.ReactNode)
  againGenerateComponent?:
    | React.ReactNode
    | ((record: RecognitionRecord, index?: number) => React.ReactNode)

  /** 是否显示AI重新生成按钮 */
  showAgainGenerate?:
    | boolean
    | ((record: RecognitionRecord, index?: number) => boolean)
  /** 是否显示隐患生成来源选择器 */
  showHazardSourceSelector?:
    | boolean
    | ((record: RecognitionRecord, index?: number) => boolean)
  /** 重新生成提示组件 */
  regenerateTip?:
    | React.ReactNode
    | ((record: RecognitionRecord, index?: number) => React.ReactNode)
}

interface XCEditableTableProps {
  dataSource: RecognitionRecord[]
  columns: XCEditableColumn[]
  onSave: (
    record: RecognitionRecord,
    rowIndex: number,
    colIndex?: number
  ) => Promise<void>
  onEditStart?: (
    record: RecognitionRecord,
    rowIndex: number,
    colIndex: number
  ) => void

  /** 隐患生成来源模式变化回调 */
  onHazardSourceModeChange?: (
    selectedIndex: number,
    record: RecognitionRecord,
    fieldName: string,
    rowIndex: number
  ) => Promise<void>
  loading?: boolean
  /**
   * 重新识别
   */
  onAgainGenerate?: (
    record: RecognitionRecord,
    fieldName: string,
    rowIndex: number
  ) => Promise<void>
}

/**
 * 可编辑行组件 - 使用 Form.useForm() hooks 模式
 */
const EditableRow: React.FC<any> = ({ index, ...props }) => {
  const [form] = Form.useForm()
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

const EditableFormRow = EditableRow

/**
 * 可编辑单元格组件
 */
const EditableCell: React.FC<any> = ({
  editable,
  dataIndex,
  title,
  record,
  index,
  colIndex,
  handleSave,
  onEditStart,
  onAgainGenerate,
  onHazardSourceModeChange,
  children,
  inputType = 'text',
  required = false,
  column,
  isRowHovered = false,
  onRowHover,
  againGenerateComponent,
  feedbackComponent, // 显式提取feedbackComponent
  ...restProps
}) => {
  const [editing, setEditing] = useState(false)
  const [hazardSourceMode, setHazardSourceMode] = useState<XCHazardSourceMode>(
    () => {
      // 检查是否为水印模式：selectedHazardSource 为数字索引，需要检查对应的 hazardSource 数组项的 val 值
      const hazardSourceIndex = record.selectedHazardSource
      const hazardSourceArray = record.hazardSourceList || []
      const selectedHazardSourceItem = hazardSourceArray[hazardSourceIndex]
      const isWatermarkMode =
        selectedHazardSourceItem &&
        selectedHazardSourceItem.name === 'watermark'

      return isWatermarkMode
        ? XCHazardSourceMode.watermark
        : XCHazardSourceMode.image
    }
  )
  const inputRef = useRef<any>(null)
  const form = useContext(EditableContext)
  const originalValue = useRef(record[dataIndex])

  // 当record数据变化时更新原始值
  useEffect(() => {
    originalValue.current = record[dataIndex]
  }, [record[dataIndex]])

  /**
   * 切换编辑状态
   */
  const toggleEdit = () => {
    const newEditing = !editing
    setEditing(newEditing)
    if (newEditing) {
      // 触发编辑开始回调
      if (onEditStart) {
        onEditStart(record, index, colIndex)
      }

      // 使用更可靠的方式将光标定位到文本末尾
      setTimeout(() => {
        if (
          inputRef.current &&
          inputRef.current.resizableTextArea &&
          inputRef.current.resizableTextArea.textArea
        ) {
          const textArea = inputRef.current.resizableTextArea.textArea
          const length = textArea.value ? textArea.value.length : 0

          // 先聚焦
          inputRef.current.focus()

          // 然后设置光标位置
          textArea.selectionStart = length
          textArea.selectionEnd = length
        } else if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 100)
    }
  }

  /**
   * 保存数据 - 使用 antd 的 Promise 模式
   */
  const onTextAreaSave = async (e: any) => {
    try {
      const values = await form.validateFields([dataIndex])
      toggleEdit()

      // 处理数组字段的保存转换
      const processedValues = { ...values }
      Object.keys(processedValues).forEach((key) => {
        // 动态处理字段：根据selectedHazardSource决定使用哪个字段
        const hazardSourceIndex = record.selectedHazardSource
        const hazardSourceArray = record.hazardSourceList || []
        const selectedHazardSourceItem = hazardSourceArray[hazardSourceIndex]
        const isWatermarkMode =
          selectedHazardSourceItem &&
          selectedHazardSourceItem.name === 'watermark'

        // 对于potentialHazardList和standardList字段，将换行符分割的字符串转换为数组
        if (
          (key === 'potentialHazardList' || key === 'standardList') &&
          typeof processedValues[key] === 'string'
        ) {
          const arrayValue = processedValues[key]
            .split('\n')
            .filter((item: string) => item.trim() !== '')

          // 如果是水印模式，同时更新Content字段
          if (isWatermarkMode) {
            const contentKey = `${key}Content`
            processedValues[contentKey] = arrayValue
          }

          processedValues[key] = arrayValue
        }

        // 对于其他字段（monomerName、RectificationMeasure、questionType），也进行动态处理
        if (
          (key === 'monomerName' ||
            key === 'RectificationMeasure' ||
            key === 'questionType') &&
          typeof processedValues[key] === 'string'
        ) {
          // 如果是水印模式，同时更新Content字段
          if (isWatermarkMode) {
            const contentKey = `${key}Content`
            processedValues[contentKey] = processedValues[key]
          }
        }
      })

      handleSave({ ...record, ...processedValues })

      originalValue.current = processedValues[dataIndex] || record[dataIndex]
    } catch (error) {
      // 表单验证失败，不进行保存
      console.log('表单验证失败:', error)
    }
  }

  // 用于跟踪DatePicker是否已经触发过编辑开始事件
  const datePickerEditStartTriggered = useRef(false)

  /**
   * 处理DatePicker聚焦事件
   */
  const handleDatePickerFocus = () => {
    // 确保编辑开始事件只触发一次
    if (onEditStart && !datePickerEditStartTriggered.current) {
      onEditStart(record, index, colIndex)
      datePickerEditStartTriggered.current = true
      setEditing(true)
    }
  }

  /**
   * DatePicker专用保存方法 - antd 签名更新
   */
  const saveDatePicker = (
    date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    // 直接使用 DatePicker 的 onChange 事件提供的日期字符串
    const updatedRecord = { ...record }
    // antd 中 dateString 可能是数组（RangePicker），这里使用单选所以取第一个
    updatedRecord[dataIndex] = Array.isArray(dateString)
      ? dateString[0]
      : dateString

    console.log('保存日期字段:', dataIndex, dateString)

    // 执行保存
    handleSave(updatedRecord)

    // 保存后重置状态
    setEditing(false)
    datePickerEditStartTriggered.current = false
  }

  /**
   * 渲染反馈组件
   */
  const renderFeedbackComponent = () => {
    // if (!column?.feedbackComponent || !isRowHovered) {
    //   return null
    // }

    const feedbackComponent =
      typeof column.feedbackComponent === 'function'
        ? column.feedbackComponent(record, index)
        : column.feedbackComponent

    return (
      <div
        className="editable-cell-feedback-component"
        style={{
          position: 'absolute',
          bottom: '4px',
          right: '15px',
          zIndex: 1
        }}
      >
        {feedbackComponent}
      </div>
    )
  }

  /**
   * 渲染反馈组件
   */
  const renderRegenerateTipComponent = () => {
    if (!column?.regenerateTip || !isRowHovered) {
      return null
    }

    const regenerateTipComponent =
      typeof column.regenerateTip === 'function'
        ? column.regenerateTip(record, index)
        : column.regenerateTip
    if (!regenerateTipComponent) {
      return null
    }

    return (
      <div
        className="editable-cell-regenerate-tip-component"
        style={{
          position: 'absolute',
          bottom: '42px',
          height: '20px',
          lineHeight: '20px',
          fontSize: '10px',
          left: '17px',
          right: '17px',
          zIndex: 1,
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          color: '#999',
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4
        }}
      >
        {regenerateTipComponent}
      </div>
    )
  }

  /**
   * 渲染再次生成组件
   */
  const renderAgainGenerateComponent = () => {
    // 优先检查showAgainGenerate属性
    const shouldShowAgainGenerate =
      typeof column?.showAgainGenerate === 'function'
        ? column.showAgainGenerate(record, index)
        : column?.showAgainGenerate

    if (shouldShowAgainGenerate && isRowHovered) {
      // 使用showAgainGenerate属性时，渲染内置的AI重新生成按钮
      const handleAgainGenerate = async () => {
        if (onAgainGenerate && dataIndex) {
          try {
            await onAgainGenerate(record, dataIndex as string, index)
          } catch (error) {
            console.error('AI重新识别失败:', error)
            message.error('AI重新识别失败')
          }
        }
      }

      return (
        <div
          className="editable-cell-again-generate-component"
          style={{
            position: 'absolute',
            right: '-22px',
            zIndex: 1
          }}
        >
          <Button
            type="link"
            size="small"
            onClick={handleAgainGenerate}
            style={{
              height: '20px',
              lineHeight: '20px',
              fontSize: '12px'
            }}
            title="修改存在的隐患，重新生成"
          >
            <div
              style={{
                height: '24px',
                width: '24px',
                background:
                  'linear-gradient(45deg, rgba(1, 146, 253, 0.7) 0%, rgba(255, 111, 243, 0.7) 100%)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src={iconRelevance}
                alt="修改存在的隐患，重新生成"
                style={{
                  width: '20px',
                  height: '20px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </Button>
        </div>
      )
    }

    // 兼容原有的againGenerateComponent属性
    if (!column?.againGenerateComponent) {
      return null
    }

    const againGenerateComponent =
      typeof column.againGenerateComponent === 'function'
        ? column.againGenerateComponent(record, index)
        : column.againGenerateComponent

    return (
      <div
        className="editable-cell-again-generate-component"
        style={{
          position: 'absolute',
          right: '-14px',
          zIndex: 1
        }}
      >
        {againGenerateComponent}
      </div>
    )
  }

  /**
   * 处理隐患生成来源模式变化
   */
  const handleHazardSourceModeChange = (selectedIndex: number) => {
    // 根据选中的索引获取对应的模式
    const hazardSourceArray = record.hazardSourceList || []
    const selectedItem = hazardSourceArray[selectedIndex]
    const mode =
      selectedItem && selectedItem.name === 'watermark'
        ? XCHazardSourceMode.watermark
        : XCHazardSourceMode.image

    setHazardSourceMode(mode)
    console.log('handleHazardSourceModeChange', mode)
    if (onHazardSourceModeChange) {
      onHazardSourceModeChange(selectedIndex, record, dataIndex, index)
    }
  }

  /**
   * 获取selectedHazardSource索引值
   */
  const getSelectedHazardSourceIndex = () => {
    if (!record.hazardSourceList || record.selectedHazardSource === undefined) {
      return 0
    }
    // selectedHazardSource 现在直接存储索引值
    return record.selectedHazardSource
  }

  /**
   * 渲染隐患生成来源选择器
   */
  const renderHazardSourceSelector = () => {
    // 检查是否需要显示隐患生成来源选择器
    const shouldShow =
      typeof column?.showHazardSourceSelector === 'function'
        ? column.showHazardSourceSelector(record, index)
        : column?.showHazardSourceSelector

    if (
      !shouldShow ||
      !isRowHovered ||
      (index === 0 && record.status !== XC_RECOGNITION_STATUS.COMPLETED)
    ) {
      return null
    }

    return (
      <div
        className="editable-cell-hazard-source-selector"
        style={{
          position: 'absolute',
          top: '0px',
          left: '0px',
          zIndex: 1
        }}
      >
        <HazardSourceSelector
          options={record.hazardSourceList}
          value={getSelectedHazardSourceIndex()}
          onChange={handleHazardSourceModeChange}
          style={{ marginBottom: 0 }}
        />
      </div>
    )
  }

  /**
   * 获取初始值 - 提取为独立函数供 DatePicker 和 TextArea 共用
   */
  const getInitialValue = () => {
    // 动态获取字段值
    const hazardSourceArray = record.hazardSourceList || []
    const selectedIndex = record.selectedHazardSource ?? 0
    const selectedHazardSourceItem = hazardSourceArray[selectedIndex]
    const isWatermarkMode =
      selectedHazardSourceItem && selectedHazardSourceItem.name === 'watermark'
    let value

    // 如果是水印模式，只使用Content字段，不回退到普通字段
    if (isWatermarkMode) {
      const contentFieldName = `${dataIndex}Content`
      value = record[contentFieldName] || null
    } else {
      value = record[dataIndex]
    }

    // 对于potentialHazardList和standardList字段，将数组转换为换行符分割的字符串
    if (
      (dataIndex === 'potentialHazardList' || dataIndex === 'standardList') &&
      Array.isArray(value)
    ) {
      return value.join('\n')
    }
    // 如果默认值是"待填写"、"待识别"、"待选择"等占位符，返回空字符串
    if (
      typeof value === 'string' &&
      (value === '待填写' || value === '待识别' || value === '待选择')
    ) {
      return ''
    }
    return value
  }

  // 当进入编辑模式或 record 变化时，设置表单初始值
  useEffect(() => {
    if (form && dataIndex) {
      const initialValue =
        inputType === 'date'
          ? record[dataIndex] && dayjs(record[dataIndex]).isValid()
            ? dayjs(record[dataIndex])
            : null
          : getInitialValue()
      form.setFieldsValue({ [dataIndex]: initialValue })
    }
  }, [editing, record, dataIndex, form])

  /**
   * 渲染单元格内容
   */
  const renderCell = () => {
    if (inputType === 'date') {
      // 获取列配置中的datePickerProps
      const datePickerProps =
        column && column.datePickerProps ? column.datePickerProps(record) : {}

      return (
        <div>
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required,
                message: `请选择${title}`
              }
            ]}
          >
            <DatePicker
              ref={inputRef}
              onChange={saveDatePicker}
              onFocus={handleDatePickerFocus}
              format="YYYY-MM-DD"
              placeholder={`请选择`}
              {...datePickerProps}
            />
          </Form.Item>
        </div>
      )
    }

    if (editing) {
      return (
        <div style={{ position: 'relative' }}>
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required,
                message: `请输入${title}`
              }
            ]}
          >
            <TextArea
              ref={inputRef}
              onBlur={onTextAreaSave}
              placeholder={`请输入`}
              allowClear={true}
              className="editable-cell-textarea"
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '150px',
                maxHeight: '150px'
              }}
            />
          </Form.Item>
        </div>
      )
    }

    return (
      <div style={{ position: 'relative' }}>
        <div
          className={`editable-cell-value-wrap ${
            // 检测children中是否包含RecognizingAnimation组件
            React.Children.toArray(children).some(
              (child: any) =>
                child?.type?.name === 'RecognizingAnimation' ||
                (child?.props?.className &&
                  child.props.className.includes('recognizing-animation'))
            )
              ? 'has-loading-animation'
              : ''
          }`}
          onClick={toggleEdit}
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <td {...restProps} style={{ position: 'relative', ...restProps.style }}>
      {renderHazardSourceSelector()}
      {editable ? renderCell() : children}
      {renderRegenerateTipComponent()}
      {renderFeedbackComponent()}
      {renderAgainGenerateComponent()}
    </td>
  )
}

/**
 * 可编辑表格组件
 */
const EditableTable = React.forwardRef<any, XCEditableTableProps>(
  (
    {
      dataSource,
      columns,
      onSave,
      onEditStart,
      onHazardSourceModeChange,
      onAgainGenerate
    },
    ref
  ) => {
    // 行级别的hover状态管理
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null)
    // 使用传入的ref替代内部ref
    const tableRef = useRef<any>(null)

    // 将内部方法暴露给父组件
    React.useImperativeHandle(ref, () => ({
      scrollToTop: scrollToTop
    }))
    const observerRef = useRef<IntersectionObserver | null>(null)

    /**
     * 清理观察器
     */
    const cleanupObserver = useCallback(() => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }, [])

    // 组件挂载时设置观察器
    useEffect(() => {
      return () => cleanupObserver()
    }, [cleanupObserver])

    // 当数据源变化时重新设置观察器
    useEffect(() => {
      cleanupObserver()
    }, [dataSource, cleanupObserver])

    /**
     * 滚动到表格顶部
     */
    const scrollToTop = () => {
      if (tableRef.current) {
        // Ant Design Table 组件的 ref 是组件实例，需要通过 nativeElement 或其他方式访问 DOM
        const tableElement = tableRef.current.nativeElement || tableRef.current
        if (tableElement && tableElement.querySelector) {
          const tableBody = tableElement.querySelector('.ant-table-body')
          if (tableBody) {
            tableBody.scrollTop = 0
          }
        } else {
          // 如果无法通过 ref 访问，尝试通过 document 查找
          const tableBody = document.querySelector('.ant-table-body')
          if (tableBody) {
            tableBody.scrollTop = 0
          }
        }
      }
    }

    /**
     * 处理保存数据
     */
    const handleSave = async (
      row: RecognitionRecord,
      index: number,
      colIndex?: number
    ) => {
      try {
        // 处理日期字段格式化
        const formattedRow = { ...row }
        if (formattedRow.deadline && dayjs.isDayjs(formattedRow.deadline)) {
          formattedRow.deadline = (formattedRow.deadline as dayjs.Dayjs).format(
            'YYYY-MM-DD'
          )
        }
        if (formattedRow.finishTime && dayjs.isDayjs(formattedRow.finishTime)) {
          formattedRow.finishTime = (
            formattedRow.finishTime as dayjs.Dayjs
          ).format('YYYY-MM-DD')
        }

        if (row.id === -1) {
          return
        }

        await onSave(formattedRow, index, colIndex)
        // message.success('保存成功')
      } catch (error) {
        message.error('保存失败')
        console.error('Save error:', error)
      }
    }

    const _dataSource = dataSource.map((item, index) => ({
      ...item,
      index
    }))

    const components = {
      body: {
        row: EditableFormRow,
        cell: EditableCell
      }
    }

    // 列下标必须以「完整基础列」为基准，而非当前可见（已过滤）列的位置。
    // 否则在「列显示设置」隐藏某列后，可见列下标会整体偏移，
    // 而下游的 getTabName(colIndex) 仍按完整列表取值，导致 dataIndex 错位、
    // 编辑变更检测命中错误字段（hasRecordChanged 恒 false）从而静默跳过保存。
    const baseColumnIndexMap = new Map<string, number>(
      getBaseColumnsData().map((c, i) => [c.dataIndex as string, i])
    )

    const editableColumns = columns.map((col) => {
      // 以 dataIndex 在完整基础列中的位置作为稳定列下标
      const colIndex = baseColumnIndexMap.has(col.dataIndex as string)
        ? (baseColumnIndexMap.get(col.dataIndex as string) as number)
        : -1
      return {
        ...col,
        fixed: col.fixed,
        // 设置列的对齐方式
        align: col.align || 'left',
        onCell: (record: RecognitionRecord, index: number) => {
          // 处理函数类型的 editable 和 inputType
          const isEditable =
            typeof col.editable === 'function'
              ? col.editable(record, index)
              : col.editable
          const inputType =
            typeof col.inputType === 'function'
              ? col.inputType(record)
              : col.inputType

          return {
            record,
            index,
            colIndex, // 添加列索引
            editable: isEditable,
            dataIndex: col.dataIndex,
            title: col.title,
            inputType: inputType || 'text',
            column: col, // 传递完整的列配置信息
            feedbackComponent: col.feedbackComponent, // 传递feedbackComponent
            againGenerateComponent: col.againGenerateComponent, // 传递againGenerateComponent
            isRowHovered: hoveredRowIndex === index, // 传递行级别的hover状态
            onRowHover: setHoveredRowIndex, // 传递设置hover状态的函数
            handleSave: (row: RecognitionRecord) =>
              handleSave(row, index, colIndex),
            onEditStart: onEditStart,
            onAgainGenerate: onAgainGenerate,
            onHazardSourceModeChange: onHazardSourceModeChange
          }
        }
      }
    })

    return (
      <Table
        ref={tableRef}
        components={components}
        className="ai-security-editable-table"
        rowClassName={(record: RecognitionRecord, index: number) => {
          let className = 'editable-row'
          if (record.isNewRecord) {
            className += ' new-record-row'
          }
          // 添加hover状态的类名
          if (hoveredRowIndex === index) {
            className += ' row-hovered'
          }
          return className
        }}
        onRow={(record: RecognitionRecord, index?: number) => {
          return {
            onMouseEnter: () => setHoveredRowIndex(index ?? null),
            onMouseLeave: () => setHoveredRowIndex(null)
          }
        }}
        bordered
        dataSource={_dataSource}
        columns={editableColumns as any}
        pagination={false}
        rowKey="index"
        scroll={{
          x: '100%',
          y: window.innerHeight - 480,
          scrollToFirstRowOnChange: true
        }}
      />
    )
  }
)

EditableTable.displayName = 'EditableTable'

export default EditableTable
